import React, { useState } from 'react';
import { ECardType } from '../../Enums/ECardType.enum';
import ECard from '../ECard/ECard';
import {
	DocumentationFolder,
	useLazyDocumentationFolderQuery,
	useLazyDocumentationFoldersQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import { useEffectAsync } from '../../../../helpers';
import { HealthDocumentationFileRef } from '../../Interfaces/HealthDocumentationFileRef.interface';

interface Props {
	type: ECardType;
	patientId: string;
	patientsOriginalId?: string;
	/** Health documentation: hands the selected files over (e.g. to a new Dočasné úložiště package). */
	onSendFiles?: (files: HealthDocumentationFileRef[]) => void;
}

export type DocumentationFolderData = DocumentationFolder & {
	doSync?: boolean;
};

const ECardWrapper: React.FC<Props> = ({
	type,
	patientId,
	patientsOriginalId,
	onSendFiles,
}) => {
	const [getDocumentationFolders] = useLazyDocumentationFoldersQuery();
	const [getDocumentationFolder] = useLazyDocumentationFolderQuery();
	const [data, setData] = useState<DocumentationFolderData[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const getIdByECardType = async () => {
		if (type === ECardType.PATIENT_E_CARD) {
			return patientsOriginalId;
		}
		return patientId;
	};

	const loadFolders = async () => {
		setIsLoading(true);
		const userId = await getIdByECardType();
		if (userId) {
			const data = await getDocumentationFolders({
				userId,
			}).unwrap();
			if (data?.documentationFolders?.data) {
				setData(data.documentationFolders.data as DocumentationFolder[]);
			}
		}
		setIsLoading(false);
	};

	const updateFolder = async (folderId: string) => {
		const folderData = await getDocumentationFolder({
			folderId,
		}).unwrap();

		if (folderData.documentationFolder) {
			const tmpData = [...data];
			for (const folderKey in tmpData) {
				const tmpFolder = tmpData[folderKey];
				if (tmpFolder.id === folderId) {
					tmpData[folderKey] = {
						...folderData.documentationFolder,
						doSync: true,
					};
				}
			}
			setData(tmpData);
		}
	};

	useEffectAsync(async () => {
		await loadFolders();
	}, [type, patientsOriginalId]);

	if (isLoading) {
		return <Loading />;
	}

	if (type === ECardType.PATIENT_E_CARD && patientsOriginalId) {
		return (
			<ECard
				folders={data}
				targetPatientId={patientId}
				patientId={patientsOriginalId}
				reloadFolders={loadFolders}
				updateFolder={updateFolder}
				type={type}
			/>
		);
	}

	return (
		<ECard
			folders={data}
			targetPatientId={patientsOriginalId}
			patientId={patientId}
			reloadFolders={loadFolders}
			updateFolder={updateFolder}
			type={type}
			onSendFiles={onSendFiles}
		/>
	);
};

export default ECardWrapper;
